import { FC, useMemo, useState } from 'react'
import {
  RadioButton,
  SimpleModal,
  useNotifications,
  Flex
} from '@sequencehq/core-components'
import { GreyGrey80, Primary13Regular } from '@sequencehq/core-components'
import {
  IntegrationCustomerSelect,
  SelectableCustomer
} from 'Integrations/components/integrationCustomerPicker'
import { ExternalAccountDetails } from '../LinkCustomerModal/ExternalAccountDetails'
import { IntegrationCustomerTypeahead } from '../IntegrationCustomerTypeahead'
import { supportsCustomerSearch } from '../supportsCustomerSearch'
import { integrationCustomerName } from '../integrationCustomerName'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName.ts'
import { FormFields, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useMutation, useQuery } from '@sequencehq/api/utils'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import invariant from 'tiny-invariant'
import { MINUTES } from '@sequencehq/utils/dates'
import {
  getSyncErrorDescription,
  type LinkOutcome
} from 'Integrations/integrationsConfig/common/shared'

// As far as creating and linking customers to a service,
// we want to only allow doing so for services that the endpoint supports
const SUPPORTED_SERVICES = [
  'Xero',
  'QuickBooks_Online',
  'Rillet',
  'NetSuite'
] as const

const isCustomerLinkingSupported = (service: IntegrationServices) => {
  return SUPPORTED_SERVICES.includes(
    service as (typeof SUPPORTED_SERVICES)[number]
  )
}

interface Props {
  onConfirm?: () => void
  onClose: () => void
  customerId: string
  service: IntegrationServices
  serviceSpecific?: {
    fields: Array<{
      property: string
      validation: Array<any>
      initialValue: string
      component: (props: Record<string, unknown>) => JSX.Element
    }>
    formFields: FormFields<Record<string, unknown>>
    isValid: boolean
  }
}

export const LinkOrCreateCustomerModal: FC<Props> = ({
  onConfirm,
  onClose,
  customerId,
  service: _service,
  serviceSpecific
}) => {
  invariant(
    isCustomerLinkingSupported(_service),
    `Linking customers is not supported for ${_service}`
  )
  // In order for typescript to be fine with us only passing a supported subset of services
  // we cast the type right below the invariant that is a type guard
  const service = _service as (typeof SUPPORTED_SERVICES)[number]

  const serviceName = getIntegrationName(service)
  const searchesCustomers = supportsCustomerSearch(service)

  const [isLinking, setIsLinking] = useState(false)
  const { displayNotification } = useNotifications()
  const pushCustomerMutation = useMutation(
    dashboard20240730Client.postPushCustomerToExternalService
  )

  /**
   * A connector that can search its own customers is asked as the user types
   * instead, so there is nothing to list up front.
   */
  const customersOnExternalServiceResponse = useQuery(
    dashboard20240730Client.getIntegrationCustomersService,
    { service },
    {
      enabled: !searchesCustomers,
      select: res => res?.items ?? []
    }
  )

  const selectableCustomers: SelectableCustomer[] = (
    customersOnExternalServiceResponse.data ?? []
  )
    .filter(account => !account.isLinked)
    .map(account => ({
      id: account.id,
      name: integrationCustomerName(account),
      country: account.country,
      isDisabled: false,
      invalidReason: account.invalidReason,
      validationErrors: account.validationErrors,
      externalUrl: account.externalUrl,
      contacts: account.contacts
    }))

  const { fields: baseFields, queries: baseQueries } = useForm({
    value: {
      linkToAccount: '',
      createNew: false
    },
    fieldConfiguration: [
      {
        property: 'createNew'
      },
      {
        property: 'linkToAccount',
        disabled: ({ formData }) => formData.createNew,
        validation: [required]
      }
    ]
  })

  const { createNew, linkToAccount } = baseFields
  const { ...serviceSpecificFieldsConfig } = serviceSpecific?.formFields ?? {}

  const customerResponse = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: customerId
    },
    {
      enabled: !isLinking,
      select: res => ({
        /**
         * Return this so we can use it as part of the PUT, but select the
         * useful bits otherwise.
         */
        fullCustomer: res,
        name: res?.legalName ?? '',
        externalId:
          res?.integrationIds.find(int => int.service === service)?.id ?? null
      }),
      staleTime: 5 * MINUTES
    }
  )

  /**
   * Link the customer to an account on the third party service.
   */
  const linkToExistingServiceCustomer = async (): Promise<LinkOutcome> => {
    const existingCustomer = customerResponse.data?.fullCustomer
    if (!existingCustomer) {
      return { isLinked: false }
    }

    const res = await dashboardv99990101Client.putCustomer({
      id: customerId,
      body: {
        ...existingCustomer,
        contacts: existingCustomer.contacts.map(contact => ({
          email: contact.email,
          name: contact.name ?? '',
          purposes: contact.purposes,
          notificationSubscriptions: contact.notificationSubscriptions
        })),
        integrationIds: [
          ...(existingCustomer?.integrationIds ?? []).filter(
            int => int.service !== service
          ),
          {
            service,
            id: baseFields.linkToAccount.value
          }
        ]
      }
    })

    return { isLinked: !res.error }
  }

  const pushCustomerToService = async (): Promise<LinkOutcome> => {
    const dynamicFields = serviceSpecific?.fields.reduce(
      (acc, curr) => {
        const property = serviceSpecific?.formFields[curr.property]
        const hasValue =
          !!property && typeof property === 'object' && 'value' in property
        if (!hasValue) return acc
        return {
          ...acc,
          [curr.property]: hasValue ? property.value : undefined
        }
      },
      {} as Record<
        (typeof serviceSpecific)['fields'][number]['property'],
        (typeof serviceSpecific)['formFields'][number]['value']
      >
    )

    const pushParams = {
      customerId,
      service,
      data: dynamicFields
    } as DashboardApi20240730.PostPushCustomerToExternalService.Params

    try {
      await pushCustomerMutation.mutateAsync(pushParams)
      return { isLinked: true }
    } catch (error) {
      return {
        isLinked: false,
        errorDescription: getSyncErrorDescription(error)
      }
    }
  }

  const externalAccountDetails = useMemo(() => {
    const externalAccount = customersOnExternalServiceResponse.data?.find(
      account => account.id === linkToAccount.value
    )

    if (!externalAccount) {
      return null
    }

    return {
      name: externalAccount.companyName ?? '',
      externalUrl: externalAccount.externalUrl ?? ''
    }
  }, [customersOnExternalServiceResponse.data, linkToAccount.value])

  // Check form validation depending on whether we are creating a new customer or linking to an existing one
  // if linking, check just the linkToAccount field
  // if creating, check service specific fields validation
  const isFormValid = useMemo(() => {
    if (!baseQueries.formData.createNew) {
      return !!baseQueries.formData.linkToAccount
    }

    return baseQueries.isValid && (serviceSpecific?.isValid || true)
  }, [
    baseQueries.isValid,
    baseQueries.formData.createNew,
    baseQueries.formData.linkToAccount,
    serviceSpecific?.isValid
  ])

  const handleSubmit = async (): Promise<boolean> => {
    setIsLinking(true)

    const { isLinked, errorDescription } = await (baseQueries.formData.createNew
      ? pushCustomerToService()
      : linkToExistingServiceCustomer())

    if (!isLinked) {
      displayNotification(`Could not link customer to ${serviceName}`, {
        type: 'error',
        description: errorDescription
      })
      setIsLinking(false)
      return false
    }

    displayNotification(`Customer linked to ${serviceName}`, {
      type: 'success'
    })

    setIsLinking(false)
    onConfirm?.()
    return true
  }

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      data-testid="modal.linkCustomer"
      title={`Link or create ${serviceName} customer`}
      cancelButtonText="Cancel"
      submitButtonText="Create link"
      submittingButtonText="Creating link..."
      submitDisabled={!isFormValid || isLinking}
      submitting={isLinking}
      onSubmit={handleSubmit}
      isLoading={
        (!searchesCustomers && customersOnExternalServiceResponse.isPending) ||
        customerResponse.isPending
      }
      contentProps={{
        minHeight: '0px'
      }}
    >
      <Flex
        color={GreyGrey80}
        {...Primary13Regular}
        flexDirection="column"
        gap="16px"
      >
        <RadioButton
          name="createNew"
          data-testid="createNew.existing"
          label={`Select existing customer in ${serviceName}`}
          isChecked={!createNew.value}
          onChange={() => {
            createNew.onChange(false)
          }}
        />
        {!linkToAccount.disabled &&
          (searchesCustomers ? (
            <IntegrationCustomerTypeahead
              service={service}
              serviceName={serviceName}
              value={linkToAccount.value}
              onChange={linkToAccount.onChange}
              data-testid="linkToAccount"
            />
          ) : (
            <IntegrationCustomerSelect
              customers={selectableCustomers}
              value={linkToAccount.value}
              onChange={linkToAccount.onChange}
              integrationName={serviceName}
              unfilteredCustomerCount={
                customersOnExternalServiceResponse.data?.length ?? 0
              }
              allCustomersLinked={
                (customersOnExternalServiceResponse.data?.length ?? 0) > 0 &&
                selectableCustomers.length === 0
              }
              data-testid="linkToAccount"
            />
          ))}
        {externalAccountDetails && (
          <ExternalAccountDetails
            name={externalAccountDetails.name}
            externalUrl={externalAccountDetails.externalUrl}
            serviceName={serviceName}
          />
        )}
        <RadioButton
          name="createNew"
          data-testid="createNew.new"
          label={`Create new customer in ${serviceName}`}
          isChecked={baseFields.createNew.value}
          onChange={() => {
            /**
             * The typeahead only knows the customers its own search turned up,
             * so it cannot show the picked one again after being hidden here.
             * Drop the choice rather than leave a link the user cannot see.
             */
            if (searchesCustomers) {
              linkToAccount.onChange('')
            }
            baseFields.createNew.onChange(true)
          }}
        />
        {linkToAccount.disabled
          ? Object.keys(serviceSpecificFieldsConfig).map(fieldKey => {
              const field = serviceSpecific?.fields.find(
                f => f.property === fieldKey
              )

              if (!field) {
                return null
              }

              const FieldComponent = field.component

              const fieldsProps =
                serviceSpecificFieldsConfig[
                  fieldKey as keyof typeof serviceSpecificFieldsConfig
                ]
              return <FieldComponent key={fieldKey} {...fieldsProps} />
            })
          : null}
      </Flex>
    </SimpleModal>
  )
}
